import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Save, Trash2 } from "lucide-react";
import { Accordion, AccordionItem, Card, CardContent, CardHeader, CardTitle, ModernButton, ModernInput, ModernSelect, Toast, ToastType } from "../../components/ui";
import { apiService } from "../../services/api";

type LeadWebhook = {
  id: string;
  name: string;
  media: string;
  targetUrl?: string | null;
  secret: string;
  isSystem: boolean;
  isActive: boolean;
};

type MediaCatalogRow = {
  id: string;
  key: string;
  label: string;
  isSelected: boolean;
};

type WebhookDraft = {
  id?: string;
  name: string;
  media: string;
  isActive: boolean;
};

const emptyDraft: WebhookDraft = {
  name: "",
  media: "",
  isActive: true,
};

export const LeadsWebhooksPage: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<LeadWebhook[]>([]);
  const [mediaCatalog, setMediaCatalog] = useState<MediaCatalogRow[]>([]);
  const [draft, setDraft] = useState<WebhookDraft>(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const publicLeadEndpoint = useMemo(() => `${window.location.origin}/api/public/leads`, []);
  const samplePayload = useMemo(
    () =>
      JSON.stringify(
        {
          name: "Joao Silva",
          email: "joao@email.com",
          message: "Tenho interesse em SUV seminovo",
          vehicleReference: "Corolla XEi 2022, branco, ate 120 mil",
          phones: [
            {
              ddi: "55",
              ddd: "11",
              phone: "999999999",
            },
          ],
        },
        null,
        2,
      ),
    [],
  );
  const buildCurlCommand = useCallback(
    (secret: string) =>
      `curl -X POST "${publicLeadEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "x-webhook-secret: ${secret}" \\
  -d '${samplePayload}'`,
    [publicLeadEndpoint, samplePayload],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [webhooks, mediaData] = await Promise.all([
        apiService.getLeadWebhooks(),
        apiService.getLeadMediaCatalog(),
      ]);
      setItems(Array.isArray(webhooks) ? webhooks : []);
      setMediaCatalog(Array.isArray(mediaData) ? mediaData : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!draft.name.trim() || !draft.media.trim()) {
      setToast({ message: t("leadsSettingsHub.webhooks.errors.required"), type: "error" });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: draft.name.trim(),
        media: draft.media.trim(),
        isActive: draft.isActive,
      };
      if (draft.id) {
        await apiService.updateLeadWebhook(draft.id, payload);
      } else {
        await apiService.createLeadWebhook(payload);
      }
      setDraft(emptyDraft);
      await load();
      setToast({ message: t("leadsSettingsHub.webhooks.saved"), type: "success" });
    } catch {
      setToast({ message: t("leadsSettingsHub.webhooks.errors.save"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      setSaving(true);
      await apiService.deleteLeadWebhook(id);
      await load();
      setToast({ message: t("leadsSettingsHub.webhooks.removed"), type: "success" });
    } catch {
      setToast({ message: t("leadsSettingsHub.webhooks.errors.delete"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null}

      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("leadsSettingsHub.webhooks.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("leadsSettingsHub.webhooks.subtitle")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("leadsSettingsHub.webhooks.publicEndpointTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <code className="block rounded-md border border-border bg-muted/30 px-3 py-2 text-sm break-all">{publicLeadEndpoint}</code>
          <Accordion>
            <AccordionItem title={t("leadsSettingsHub.webhooks.payloadExampleTitle")} defaultOpen={false}>
              <p className="text-xs text-muted-foreground mb-2">{t("leadsSettingsHub.webhooks.payloadHint")}</p>
              <pre className="rounded-md border border-border bg-muted/30 p-3 text-xs overflow-auto">
                <code>{samplePayload}</code>
              </pre>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{draft.id ? t("leadsSettingsHub.webhooks.editTitle") : t("leadsSettingsHub.webhooks.newTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ModernInput label={t("leadsSettingsHub.webhooks.fields.name")} value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} />
            <ModernSelect
              label={t("leadsSettingsHub.webhooks.fields.media")}
              value={draft.media}
              onChange={(e) => setDraft((prev) => ({ ...prev, media: e.target.value }))}
              options={mediaCatalog.filter((m) => m.isSelected).map((m) => ({ value: m.label, label: m.label }))}
              placeholder={t("leadsSettingsHub.webhooks.fields.mediaPlaceholder")}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))} />
            {t("leadsSettingsHub.webhooks.fields.active")}
          </label>
          <div className="flex items-center gap-2">
            <ModernButton type="button" onClick={() => void save()} disabled={saving}>
              {draft.id ? <Save className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {draft.id ? t("leadsSettingsHub.webhooks.saveEdit") : t("leadsSettingsHub.webhooks.create")}
            </ModernButton>
            {draft.id ? (
              <ModernButton type="button" variant="outline" onClick={() => setDraft(emptyDraft)} disabled={saving}>
                {t("actions.cancel")}
              </ModernButton>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("leadsSettingsHub.webhooks.listTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("leadsSettingsHub.webhooks.empty")}</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="rounded-md border border-border p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.media}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ModernButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setDraft({
                          id: item.id,
                          name: item.name,
                          media: item.media,
                          isActive: item.isActive,
                        })
                      }
                      disabled={saving || item.isSystem}
                    >
                      {t("actions.edit")}
                    </ModernButton>
                    <ModernButton type="button" variant="outline" size="sm" onClick={() => void remove(item.id)} disabled={saving || item.isSystem}>
                      <Trash2 className="h-4 w-4" />
                    </ModernButton>
                  </div>
                </div>
                <Accordion>
                  <AccordionItem title={t("leadsSettingsHub.webhooks.curlExampleTitle")} defaultOpen={false}>
                    <pre className="rounded-md border border-border bg-muted/30 p-3 text-xs overflow-auto">
                      <code>{buildCurlCommand(item.secret)}</code>
                    </pre>
                  </AccordionItem>
                </Accordion>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
